import {
  CTCSS_TONES,
  DTCS_CODES,
  decodeLbcd,
  encodeLbcd,
  type RadioProgress,
  type WebChannel,
} from './baofeng-web.ts'
import type { RadioTransport } from './web-serial.ts'

const ACK = 0x06
const BLOCK_SIZE = 16
const MEMORY_SIZE = 0x1000
const CHANNEL_START = 0x10
const NAME_START = 0x08d0
const USED_START = 0x0d20
const SCAN_START = 0x0d30
const CHANNEL_COUNT = 128

export type RT23Profile = {
  protocol: 'retevis-rt23'
  id: string
  vendor: 'Retevis'
  model: 'RT23'
  summary: string
  tags: readonly string[]
  baudRate: 9600
  channelMinimum: 1
  channelMaximum: number
  nameLength: 7
  memorySize: number
  powerLevels: readonly ['Low', 'High']
}

export const RT23_PROFILES: readonly RT23Profile[] = [
  {
    protocol: 'retevis-rt23',
    id: 'retevis-rt23',
    vendor: 'Retevis',
    model: 'RT23',
    summary: 'Retevis RT23 dual-band handheld.',
    tags: ['Experimental', 'Read + write', '128 channels', '7-character names'],
    baudRate: 9600,
    channelMinimum: 1,
    channelMaximum: CHANNEL_COUNT,
    nameLength: 7,
    memorySize: MEMORY_SIZE,
    powerLevels: ['Low', 'High'],
  },
]

function emptyChannel(number: number): WebChannel {
  return {
    number,
    empty: true,
    name: '',
    rxFrequency: null,
    txFrequency: null,
    toneMode: 'none',
    ctcss: null,
    dtcs: null,
    dtcsPolarity: 'N',
    mode: 'NFM',
    power: 'Low',
    skip: false,
  }
}

function decodeTone(raw: Uint8Array): Pick<
  WebChannel,
  'toneMode' | 'ctcss' | 'dtcs' | 'dtcsPolarity'
> {
  if (raw.every((value) => value === 0xff)) {
    return { toneMode: 'none', ctcss: null, dtcs: null, dtcsPolarity: 'N' }
  }
  let value = 0
  for (let index = raw.length - 1; index >= 0; index -= 1) {
    value = value * 100 + (raw[index] >> 4) * 10 + (raw[index] & 0x0f)
  }
  if (value >= 12000) {
    return { toneMode: 'dtcs', ctcss: null, dtcs: value - 12000, dtcsPolarity: 'R' }
  }
  if (value >= 8000) {
    return { toneMode: 'dtcs', ctcss: null, dtcs: value - 8000, dtcsPolarity: 'N' }
  }
  return { toneMode: 'ctcss', ctcss: value / 10, dtcs: null, dtcsPolarity: 'N' }
}

function encodeTone(channel: WebChannel): readonly [number, number] {
  let value = 0xffff
  if (channel.toneMode === 'ctcss' && channel.ctcss) {
    value = Math.round(channel.ctcss * 10)
  } else if (channel.toneMode === 'dtcs' && channel.dtcs) {
    value = channel.dtcs + (channel.dtcsPolarity === 'R' ? 12000 : 8000)
  }
  const part = (number: number) =>
    (((Math.floor(number / 10) & 0x0f) << 4) | (number % 10)) & 0xff
  return [part(value % 100), part(Math.floor(value / 100) % 100)]
}

function bit(image: Uint8Array, start: number, number: number): boolean {
  return (image[start + ((number - 1) >> 3)] & (1 << ((number - 1) & 7))) !== 0
}

function setBit(image: Uint8Array, start: number, number: number, value: boolean): void {
  const offset = start + ((number - 1) >> 3)
  const mask = 1 << ((number - 1) & 7)
  image[offset] = value ? image[offset] | mask : image[offset] & ~mask
}

export function parseRT23Channel(
  image: Uint8Array,
  profile: RT23Profile,
  number: number,
): WebChannel {
  if (!bit(image, USED_START, number)) return emptyChannel(number)
  const start = CHANNEL_START + (number - 1) * BLOCK_SIZE
  const raw = image.slice(start, start + BLOCK_SIZE)
  const rxFrequency = decodeLbcd(raw.slice(0, 4))
  if (!rxFrequency) return emptyChannel(number)
  const txRaw = raw.slice(4, 8)
  const txFrequency = txRaw.every((value) => value === 0xff)
    ? null
    : decodeLbcd(txRaw)
  const rxTone = decodeTone(raw.slice(8, 10))
  const txTone = decodeTone(raw.slice(10, 12))
  const selected = txTone.toneMode === 'none' ? rxTone : txTone
  return {
    number,
    empty: false,
    name: String.fromCharCode(...image.slice(NAME_START + (number - 1) * 8, NAME_START + (number - 1) * 8 + 7))
      .replace(/[\u0000\u00ff\ufffd]/g, ' ')
      .trim(),
    rxFrequency,
    txFrequency,
    ...selected,
    mode: raw[14] & 0x08 ? 'NFM' : 'FM',
    power: raw[14] & 0x10 ? 'High' : 'Low',
    skip: !bit(image, SCAN_START, number),
  }
}

export function parseRT23Channels(image: Uint8Array, profile: RT23Profile): WebChannel[] {
  return Array.from({ length: CHANNEL_COUNT }, (_, index) => parseRT23Channel(image, profile, index + 1))
}

export function applyRT23Channel(image: Uint8Array, profile: RT23Profile, channel: WebChannel): void {
  const start = CHANNEL_START + (channel.number - 1) * BLOCK_SIZE
  const raw = image.subarray(start, start + BLOCK_SIZE)
  const nameStart = NAME_START + (channel.number - 1) * 8
  setBit(image, USED_START, channel.number, !channel.empty)
  setBit(image, SCAN_START, channel.number, !channel.empty && !channel.skip)
  if (channel.empty) {
    raw.fill(0xff)
    image.fill(0xff, nameStart, nameStart + 7)
    return
  }
  if (!channel.rxFrequency) throw new Error('A receive frequency is required.')
  raw.fill(0)
  encodeLbcd(channel.rxFrequency, raw.subarray(0, 4))
  if (channel.txFrequency) encodeLbcd(channel.txFrequency, raw.subarray(4, 8))
  else raw.fill(0xff, 4, 8)
  const tone = encodeTone(channel)
  raw.set(tone, 8)
  raw.set(tone, 10)
  raw[14] =
    (channel.power === 'High' ? 0x10 : 0) |
    (channel.mode === 'NFM' ? 0x08 : 0)
  image.fill(0, nameStart, nameStart + 7)
  const name = channel.name.toUpperCase().replace(/[^\x20-\x7e]/g, '').slice(0, 7)
  for (let index = 0; index < name.length; index += 1) image[nameStart + index] = name.charCodeAt(index)
}

async function enter(transport: RadioTransport): Promise<void> {
  await transport.discardInput()
  await transport.write(new TextEncoder().encode('PROIUAM'))
  if ((await transport.readExact(1, 1800))[0] !== ACK) throw new Error('The radio refused RT23 programming mode.')
  await transport.write(Uint8Array.of(0x02))
  if (!new TextDecoder().decode(await transport.readExact(8, 1800)).startsWith('P31183')) throw new Error('The connected radio does not identify as Retevis RT23.')
  await transport.write(Uint8Array.of(ACK))
  if ((await transport.readExact(1, 1500))[0] !== ACK) throw new Error('The radio refused the RT23 clone session.')
}

export async function readRT23RadioWithTransport(profile: RT23Profile, transport: RadioTransport, onProgress?: (p: RadioProgress) => void): Promise<{ image: Uint8Array; reportedModel: string }> {
  await enter(transport)
  try {
    const image = new Uint8Array(MEMORY_SIZE)
    for (let address = 0; address < MEMORY_SIZE; address += BLOCK_SIZE) {
      await transport.write(
        Uint8Array.of(
          0x52,
          (address >> 8) & 0xff,
          address & 0xff,
          BLOCK_SIZE,
        ),
      )
      const reply = await transport.readExact(BLOCK_SIZE + 5, 2500)
      const data = reply.slice(4, 20)
      if (data.reduce((sum, value) => (sum + value) & 0xff, 0) !== reply[20]) throw new Error('RT23 block checksum failed.')
      image.set(data, address)
      onProgress?.({ current: address / BLOCK_SIZE + 1, total: MEMORY_SIZE / BLOCK_SIZE, label: 'Reading Retevis RT23 memory' })
    }
    return { image, reportedModel: 'RT23' }
  } finally {
    await transport.write(new TextEncoder().encode('E'))
  }
}

export async function writeRT23RadioWithTransport(profile: RT23Profile, image: Uint8Array, transport: RadioTransport, onProgress?: (p: RadioProgress) => void): Promise<void> {
  if (image.byteLength < MEMORY_SIZE) throw new Error('The selected image is smaller than RT23 memory.')
  await enter(transport)
  try {
    for (let address = 0; address < 0x0ec0; address += BLOCK_SIZE) {
      const data = image.slice(address, address + BLOCK_SIZE)
      const packet = new Uint8Array(21)
      packet.set([
        0x57,
        (address >> 8) & 0xff,
        address & 0xff,
        BLOCK_SIZE,
      ])
      packet.set(data, 4)
      packet[20] = data.reduce((sum, value) => (sum + value) & 0xff, 0)
      await transport.write(packet)
      if ((await transport.readExact(1, 1800))[0] !== ACK) throw new Error(`The radio rejected RT23 block 0x${address.toString(16)}.`)
      onProgress?.({ current: address / BLOCK_SIZE + 1, total: 0x0ec0 / BLOCK_SIZE, label: 'Writing Retevis RT23 memory' })
    }
  } finally {
    await transport.write(new TextEncoder().encode('E'))
  }
}
